<template>
  <div
    ref="root"
    role="dialog"
    aria-modal="false"
    :aria-label="i18n(title)"
    data-journey-card
    class="raw-glass pointer-events-auto relative grid gap-20 rounded-[1.5rem] p-20 [@media(max-height:700px)]:gap-12"
  >
    <!-- Out of the flow: in it, the ✕ either squeezes the title or takes a row. -->
    <button
      type="button"
      class="raw-glass rounded-full absolute right-16 top-16 flex h-36 w-36 cursor-pointer items-center justify-center text-[--raw-verydarkgrey] transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] active:border-[white] active:bg-[white] md:hover:border-[white] md:hover:bg-[white]"
      :aria-label="i18n(translations.exit)"
      @click="emit('close')"
    >
      <svg
        width="10"
        height="10"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M1 1L11 11M11 1L1 11"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
        />
      </svg>
    </button>

    <div class="mr-8 grid gap-4 pr-32">
      <h2 class="raw-heading-2 text-balance">
        {{ i18n(title) }}
      </h2>

      <span
        v-if="place"
        class="raw-body-3 line-clamp-1 text-[--raw-verydarkgrey]"
      >
        {{ i18n(place) }}
      </span>

      <!-- Glued shut: whitespace before the marker renders as a literal space. -->
      <p class="raw-body-2 mt-8 text-[--raw-verydarkgrey]">
        {{ i18n(body) }}<SourceMarker :numbers="citations" zone="journey" />
      </p>
    </div>

    <!-- The client's order: body, diagram, body. -->
    <PhoneDiagram v-if="parts" :parts />

    <p v-if="bodyAfter" class="raw-body-2 text-[--raw-verydarkgrey]">
      {{ i18n(bodyAfter) }}
    </p>

    <div class="grid grid-cols-[1fr_auto_1fr] items-center gap-8 md:gap-16">
      <button
        v-if="step > 1"
        type="button"
        class="raw-cta-3 col-start-1 cursor-pointer justify-self-start opacity-40 transition-opacity duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:opacity-70"
        @click="emit('back')"
      >
        {{ i18n(translations.back) }}
      </button>

      <Button
        variant="tertiary"
        data-journey-next
        class="col-start-2 min-w-[200px] justify-self-center border-[--raw-black] bg-[--raw-black] px-24 py-10 transition-colors duration-[var(--raw-fast)] ease-[var(--raw-ease-out-quart)] hover:border-[--raw-verydarkgrey] hover:bg-[--raw-verydarkgrey] active:border-[--raw-verydarkgrey] active:bg-[--raw-verydarkgrey]"
        @click="emit('next')"
      >
        <!-- Colour on the span: on the button it ties with the DS variant. -->
        <span class="raw-cta-1 text-[white]">{{ i18n(cta) }}</span>
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import Button from '~/scopes/campaign/components/UI/Button.vue'
import PhoneDiagram from '~/scopes/campaign/components/UI/PhoneDiagram.vue'
import SourceMarker from '~/scopes/campaign/components/UI/SourceMarker.vue'
import type {
  I18nDescriptor,
  PhonePart,
} from '~/scopes/campaign/data/metals/types'

import translations from './JourneyOverlay.translations'

// The camera's hidden twin renders this same component from the same props, so every
// optional below has to reach both — `JourneyOverlay` binds one object to each.
const props = defineProps<{
  title: I18nDescriptor
  place?: I18nDescriptor
  body: I18nDescriptor
  citations?: readonly number[]
  /** The phone dissection, lit for the journey's metal — the opening beat only. */
  parts?: readonly PhonePart[]
  bodyAfter?: I18nDescriptor
  cta: I18nDescriptor
  /** The 1-based index, not the URL's token — only Back reads it. */
  step: number
  /** Take focus on mount: the caller was driving the CTA from the keyboard. */
  focusCta?: boolean
}>()

const emit = defineEmits<{ next: []; back: []; close: [] }>()

const i18n = useI18n()
const root = ref<HTMLElement | null>(null)

// Not the Transition's enter hook: it never fires across the flight's landing wait.
onMounted(() => {
  if (props.focusCta)
    root.value?.querySelector<HTMLElement>('[data-journey-next]')?.focus()
})
</script>
